import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AnimatedNumbers from "react-animated-numbers";

// Assets & Utils
import { colors } from "../../values/colors";
import defaultProfile from "../../assets/images/defaultProfile2.webp";
import { icons } from "../../assets/icons/icons";
import { ReactComponent as VisibilityIcon } from "../../assets/icons/visibility.svg";
import {
  formatTimestampToReadableDate,
  getAgeFromTimestamp,
  getDobFromTimestamp,
} from "../../uttils/common";
import { getUserPref } from "../../uttils/UserPref";

// API Services
import {
  useBidMutation,
  useDeclareMutation,
  usePassPlayerMutation,
  usePickNextPlayerMutation,
  useReAuctionMutation,
  useSellPlayerMutation,
  useUndoPlayerMutation,
  useGetAllRolesQuery,
} from "../../services/auctionAPI";
import { useLazyGetSpecificTournamentQuery } from "../../services/tournamentsAPI";

// Components & Modals
import PassPlayerModal from "../../components/Modal/PassPlayerModal";
import SuccessModal from "../../components/Modal/SuccessModal";
import NextPlayerModal from "../../components/Modal/NextPlayerModal";
import SoldModal from "../../components/Modal/SoldModal";
import SoldConfirmModal from "../../components/Modal/SoldConfirmModal";
import AuctionSetting from "./AuctionSetting";
import CustomBreadCrumbs from "./CustomBreadCrumbs";
import SearchInput from "../../components/SearchInput";
import PlayerCard from "../../components/PlayerCard";
import PlayerAuctionCard from "../../components/PlayerAuctionCard";
import Loader from "../../components/Loader";
import EndAuctionModal from "../../components/Modal/EndAuctionModal";
import NoDataView from "../../components/NoDataView";
import ProfileModal from "../../components/Modal/ProfileModal";
import ConfirmModal from "../../components/Modal/ConfirmModal";
import WarningModal from "../../components/Modal/WarningModal";

const AuctionBidding = () => {
  const userData = getUserPref();
  const { tournamentId } = JSON.parse(userData);
  const navigate = useNavigate();

  // API Hooks
  const [bid, { isLoading: isBidLoading }] = useBidMutation();
  const [passPlayer, { isLoading: isPassLoading }] = usePassPlayerMutation();
  const [pickNextPlayer, { data: pickNextPlayerData, isLoading: pickLoading, error: pickNextPlayerError }] = usePickNextPlayerMutation();
  const [triggerGetTournament, { data: tournamentDetails, isLoading }] = useLazyGetSpecificTournamentQuery();
  const [sellPlayer, { isLoading: sellLoading }] = useSellPlayerMutation();
  const [declare] = useDeclareMutation();
  const [reAuction] = useReAuctionMutation();
  const [undoPlayer, { isLoading: undoLoading }] = useUndoPlayerMutation();
  const { data: rolesData } = useGetAllRolesQuery();

  // State
  const [player, setPlayer] = useState(() => {
    const stored = sessionStorage.getItem("currentPlayer");
    return stored ? JSON.parse(stored) : null;
  });
  const [currBid, setCurrBid] = useState(player?.currentBid);
  const [baseBid, setBaseBid] = useState(player?.basePrice);
  const [passModal, setPassModal] = useState(false);
  const [passSuccessModal, setPassSuccessModal] = useState(false);
  const [soldSuccessModal, setSoldSuccessModal] = useState(false);
  const [nextPlayerModal, setNextPlayerModal] = useState(false);
  const [soldModal, setSoldModal] = useState(false);
  const [confirmSoldModal, setCofirmSoldModal] = useState(false);
  const [selectTeam, setSelectTeam] = useState({});
  const [playerCounts, setPlayerCounts] = useState({});
  const [search, setSearch] = useState("");
  const [searchPlayers, setSearchPlayers] = useState([]);
  const [endModal, setEndModal] = useState(false);
  const [profileModal, setProfileModal] = useState(false);
  const [undoModal, setUndoModal] = useState(false);
  const [endPurchase, setEndPurchase] = useState(false);

  // Memos & Helpers
  const roles = rolesData?.data || [];
  const roleName = roles?.find((r) => r?._id === player?.playerId?.roleId)?.name;
  const playersSold = tournamentDetails?.data?.tournament?.teamMembers?.filter((m) => m?.isSold).length;
  const playersUnsold = tournamentDetails?.data?.tournament?.teamMembers?.filter((m) => m?.isPassed).length;
  const auctionId = useMemo(() => tournamentDetails?.data?.tournament?.auctionDetails?._id, [tournamentDetails]);
  const teamMembers = tournamentDetails?.data?.tournament?.teamMembers;
  const isJitoTournament = tournamentDetails?.data?.tournament?.isJitoTournament;

  // console.log("isJitoTournament", isJitoTournament)

  const gradientTextStyle = {
    background: colors.primary,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontWeight: "700",
  };


  const isAllPlayerPurchased = () => {
    const totalPlayerCount = tournamentDetails?.data?.tournament?.teamPlayerCount;
    const teams = tournamentDetails?.data?.tournament?.auctionDetails?.auctionTeams;
    if (!teams || teams.length === 0 || !totalPlayerCount) return false;
    const members = tournamentDetails?.data?.tournament?.teamMembers || [];
    const counts = {};
    members.forEach((p) => {
      if (p?.teamId) counts[p.teamId] = (counts[p.teamId] || 0) + 1;
    });
    return teams.every((team) => (counts[team.teamId] || playerCounts[team.teamId] || 0) >= totalPlayerCount);
  };

  // Logic Handlers
  const bidUp = async () => {
    const payload = { tournamentId, auctionId, memberId: player?._id, increaseBid: true };
    try {
      const result = await bid(payload);
      if (result?.data?.success) {
        setPlayer(result?.data?.data?.teamMember);
        setCurrBid(result?.data?.data?.teamMember?.currentBid);
      } else {
        toast.error(result?.error?.data?.message || "Something went wrong");
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  const bidDown = async () => {
    if (currBid === baseBid) return;
    const payload = { tournamentId, auctionId, memberId: player?._id, increaseBid: false };
    try {
      const result = await bid(payload);
      if (result?.data?.success) {
        setPlayer(result?.data?.data?.teamMember);
        setCurrBid(result?.data?.data?.teamMember?.currentBid);
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  const handlePass = async () => {
    const payload = { tournamentId, auctionId, memberId: player?._id };
    try {
      const result = await passPlayer(payload);
      if (result?.data?.success) {
        setPassSuccessModal(true);
        await triggerGetTournament({ tournamentId });
        setTimeout(() => {
          handleNextPlayer();
          setPassSuccessModal(false);
        }, 2000);
      }
      setPassModal(false);
    } catch (error) {
      setPassModal(false);
    }
  };

  const handleNextPlayer = async () => {
    setPassSuccessModal(false);
    if (isAllPlayerPurchased()) {
      setNextPlayerModal(false);
      setEndPurchase(true);
      return;
    }
    try {
      const res = await pickNextPlayer({ tournamentId, auctionId, manual: false });
      if (res?.data?.success) {
        if (isAllPlayerPurchased()) {
          setNextPlayerModal(false);
          setEndPurchase(true);
          return;
        }
        setPlayer(res?.data?.data?.teamMember);
        setNextPlayerModal(true);
      } else {
        isAllPlayerPurchased() ? setEndPurchase(true) : setEndModal(true);
      }
    } catch (error) {
      toast.error("Error fetching next player");
    }
  };

  const handleManualNextPlayer = async (playerId) => {
    try {
      const res = await pickNextPlayer({ tournamentId, auctionId, manual: true, memberId: playerId });
      if (res?.data?.success) setPlayer(res?.data?.data?.teamMember);
      setSearch("");
    } catch (error) {
      setSearch("");
    }
  };

  const handleSold = async () => {
    setCofirmSoldModal(false);
    setSoldModal(false);
    const payload = { tournamentId, auctionId, memberId: player?._id, teamId: selectTeam?.teamId };
    try {
      const res = await sellPlayer(payload);
      if (res?.data?.success) {
        await triggerGetTournament({ tournamentId });
        setSoldSuccessModal(true);
        setTimeout(() => {
          handleNextPlayer();
          setSoldSuccessModal(false);
          setSelectTeam({});
        }, 2000);
      } else {
        toast.error(res?.error?.data?.message || res?.data?.message || "Sale failed");
      }
    } catch (error) {
      toast.error("Sale failed");
    }
  };

  const handleEnd = async () => {
    try {
      await declare({ isPaused: false, isEnd: true, reason: "Auction Ended Successfully", auctionId });
      navigate("/auction/auction-details");
    } catch (error) {
      navigate("/auction/auction-details");
    }
  };

  const handleReauction = async () => {
    try {
      await reAuction({ auctionId });
      pickNextPlayer({ tournamentId, auctionId, manual: false });
      triggerGetTournament({ tournamentId });
    } catch (error) {
      toast.error("Re-auction failed");
    }
  };

  const handleUndo = async () => {
    try {
      const res = await undoPlayer({ auctionId });
      if (res?.data?.success) {
        setPlayer(res?.data?.data?.resultMember);
        setBaseBid(res?.data?.data?.resultMember?.basePrice);
        setCurrBid(res?.data?.data?.resultMember?.currentBid);
        triggerGetTournament({ tournamentId });
      }
      setUndoModal(false);
    } catch (error) {
      setUndoModal(false);
    }
  };

  const debounce = (fn, delay) => {
    let timer;
    return (...args) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  };

  const bidUpDebounce = debounce(bidUp, 300);
  const bidDownDebounce = debounce(bidDown, 300);

  // Effects
  useEffect(() => { triggerGetTournament({ tournamentId }); }, [tournamentId]);

  useEffect(() => {
    if (auctionId && !player) {
      pickNextPlayer({ tournamentId, auctionId, manual: false });
    }
  }, [auctionId]);

  useEffect(() => {
    const isEnd = tournamentDetails?.data?.tournament?.auctionDetails?.isEnd;
    const allPurchased = isAllPlayerPurchased();
    if (allPurchased) {
      setNextPlayerModal(false);
      setEndPurchase(true);
      setEndModal(false);
    } else if (!isEnd && pickNextPlayerError) {
      declare({ isPaused: false, isEnd: true, reason: "Auction Ended Successfully", auctionId });
      setEndModal(true);
    } else {
      setEndPurchase(false);
      setEndModal(isEnd || !!pickNextPlayerError);
    }
  }, [tournamentDetails, pickNextPlayerError, playerCounts]);

  useEffect(() => {
    if (pickNextPlayerData?.data?.teamMember) {
      const data = pickNextPlayerData.data.teamMember;
      setPlayer(data);
      setBaseBid(data.basePrice);
      setCurrBid(data.currentBid);
    }
  }, [pickNextPlayerData]);

  useEffect(() => {
    if (tournamentDetails?.data?.tournament) {
      const counts = {};
      tournamentDetails.data.tournament.teamMembers.forEach((p) => {
        if (p?.teamId) counts[p.teamId] = (counts[p.teamId] || 0) + 1;
      });
      setPlayerCounts(counts);
    }
  }, [tournamentDetails]);

  useEffect(() => {
    const members =
      tournamentDetails?.data?.tournament?.teamMembers.filter(
        (e) => !e?.isSold && !e?.isPassed
      ) || [];
    const term = search.trim().toLowerCase();

    if (!term) {
      setSearchPlayers([]);
      return;
    }

    const isNumeric = !isNaN(term) && !isNaN(parseFloat(term));

    setSearchPlayers(
      members.filter((p) => {
        const nameMatch =
          p?.playerName?.toLowerCase().includes(term) ||
          p?.playerId?.name?.toLowerCase().includes(term);

        const serialMatch = isNumeric
          ? Number(p?.serialNo) === Number(term)
          : String(p?.serialNo || "").toLowerCase() === term;

        return nameMatch || serialMatch;
      })
    );
  }, [search, tournamentDetails]);

  useEffect(() => {
    if (player) sessionStorage.setItem("currentPlayer", JSON.stringify(player));
  }, [player]);

  if (pickLoading || isLoading) return <Loader />;

  console.log("isJitoTournament")


  return (
    <Box sx={{
      px: "3rem", py: "1.5rem", bgcolor: "#FFFFFF", minHeight: "100vh",
      position: "relative",
      zIndex: 1
    }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end", // This ensures vertical centering
          gap: "2rem"
        }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <CustomBreadCrumbs
            tournamentName={tournamentDetails?.data?.tournament?.name}
            auctionId={auctionId}
          />
        </Box>

        <Box sx={{ width: "40%", maxWidth: "500px", paddingBottom: '0.5rem', justifyContent: "flex-end", alignItems: "center" }}> {/* Adjusted width for better balance */}
          <SearchInput
            placeholder="Search Players"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Box>
      </Box>

      <Box sx={{ border: 1, borderColor: "#D7D7D7", borderRadius: 2, p: 2, mt: "1rem" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontWeight: "500", fontSize: "1.75rem", color: "#1A1B23" }}>
            Player Details
          </Typography>
          <Box sx={{ display: "flex", gap: "1rem", width: "50%", justifyContent: "flex-end" }}>

            <AuctionSetting
              auctionId={auctionId}
              tournamentId={tournamentId}
              auctionDetails={
                tournamentDetails?.data?.tournament?.auctionDetails
              }
            />
          </Box>
        </Box>

        {search.length > 0 ? (
          <Grid container columnSpacing={2} rowSpacing={3}>
            {searchPlayers.length > 0 ? (
              searchPlayers.map((p) => (
                <Grid item xs={12} sm={6} md={3} key={p._id}>
                  <PlayerCard
                    name={p.playerName}
                    role={p?.playerId?.roleId || "Player"}
                    image={p?.playerId?.profilePic || defaultProfile}
                    onClick={() => handleManualNextPlayer(p?._id)}
                  />
                </Grid>
              ))
            ) : <NoDataView text="No players found" />}
          </Grid>
        ) : (
          !endPurchase && !endModal && (
            <Grid container spacing={2} sx={{ mt: "0.5rem" }}>
              <Grid item xs={12} lg={8}>
                <PlayerAuctionCard
                  player={player}
                  playerNumber={teamMembers?.find(e => e?.playerId?._id === player?.playerId?._id)?.playerId?.uniqueId || "1244"}
                  roleName={roleName}
                  isJitoTournament={isJitoTournament}
                  showVisibilityIcon={true}
                  onViewProfile={() => setProfileModal(true)}
                />
              </Grid>

              <Grid item xs={12} lg={4}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <Box sx={{ border: "1px solid #E0E0E0", borderRadius: "12px", bgcolor: "white", overflow: "hidden" }}>
                    <Box sx={{ display: "flex", py: 4 }}>
                      <Box sx={{ flex: 1, textAlign: "center", borderRight: "1px solid #E0E0E0" }}>
                        <Typography sx={{ fontWeight: "500", fontSize: "1.8rem" }}>Base Price</Typography>
                        <Typography
                          sx={{
                            color: colors.accent,
                            fontWeight: "600",
                            fontSize: "2.5rem",
                          }}
                        >
                          {baseBid}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, textAlign: "center" }}>
                        <Typography sx={{ fontWeight: "500", fontSize: "1.8rem" }}>Current Bid</Typography>
                        <Typography
                          sx={{
                            color: colors.accent,
                            fontWeight: "600",
                            fontSize: "1.5rem",
                          }}
                        >
                          <AnimatedNumbers
                            transitions={(index) => ({
                              type: "none",
                              duration: 0.3,
                            })}
                            animateToNumber={currBid}
                            fontStyle={{
                              fontSize: "2.5rem",
                              backgroundColor: colors.primary,
                              fontWeight: "600",
                            }}
                          />
                        </Typography>
                      </Box>
                    </Box>

                    <Divider />

                    <Box sx={{ p: 3 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Button
                            fullWidth
                            sx={{
                              background: colors.primary,
                              color: "white",
                              borderRadius: "2rem",
                              py: 1.2,
                              textTransform: "none",
                              fontFamily: 'Urbanist',
                              fontSize: "1.2rem",
                              height: "3.5rem",
                              fontWeight: "200",
                              // "&:hover": { background: "#d66d2a" }
                            }}
                            onClick={bidUpDebounce}
                            disabled={isBidLoading}
                            startIcon={<img src={icons.arrowup} alt="up" style={{ width: 14 }} />}
                          >
                            Bid Up
                          </Button>
                        </Grid>
                        <Grid item xs={6}>
                          <Button
                            fullWidth
                            sx={{
                              background: currBid === baseBid ? "#A0A0A0" : colors.primary,
                              color: "white",
                              borderRadius: "2rem",
                              py: 1.2,
                              textTransform: "none",
                              height: "3.5rem",
                              fontFamily: 'Urbanist',
                              fontSize: "1.2rem",
                              fontWeight: "200",
                              // "&:hover": { background: currBid === baseBid ? "#A0A0A0" : "#d66d2a" }
                            }}
                            onClick={bidDownDebounce}
                            disabled={isBidLoading}
                            startIcon={<img src={icons.arrowdown} alt="down" style={{ width: 14 }} />}
                          >
                            Bid Down
                          </Button>
                        </Grid>
                        <Grid item xs={6}>
                          <Button
                            fullWidth
                            sx={{
                              background: "#49E07C",
                              color: "white",
                              borderRadius: "2rem",
                              height: "3.5rem",
                              py: 1.2,
                              textTransform: "none",
                              fontFamily: 'Urbanist',
                              fontSize: "1.2rem",
                              fontWeight: "200",
                              "&:hover": { background: "#41c46b" }
                            }}
                            onClick={() => setSoldModal(true)}
                          >
                            Sold
                          </Button>
                        </Grid>
                        <Grid item xs={6}>
                          <Button
                            fullWidth
                            sx={{
                              background: "#FF0140",
                              color: "white",
                              borderRadius: "2rem",
                              py: 1.2,
                              textTransform: "none",
                              fontFamily: 'Urbanist',
                              fontSize: "1.2rem",
                              height: "3.5rem",
                              fontWeight: "200",
                              "&:hover": { background: "#d90036" }
                            }}
                            onClick={() => setPassModal(true)}
                          >
                            Pass
                          </Button>
                        </Grid>
                      </Grid>

                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                        <Button
                          sx={{
                            // background: colors.primary,
                            borderRadius: "1.5rem",
                            py: "0.6rem",
                            textTransform: "capitalize",
                            fontSize: "1rem",
                            color: "white",
                            backgroundColor: "black",
                            width: "12rem",
                            gap: ".2rem",
                          }}
                          onClick={() => setUndoModal(true)}
                        >
                          Undo
                        </Button>
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    onClick={() => navigate("/auction/auction-data")}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2.5,
                      border: "1px solid #E0E0E0",
                      borderRadius: "12px",
                      bgcolor: "white",
                      cursor: "pointer",
                      "&:hover": { ...gradientTextStyle }
                    }}
                  >
                    <Typography sx={{ fontWeight: "500", fontSize: "1.5rem", color: "#1A1B23" }}>
                      Auction Data
                    </Typography>
                    <Box sx={{
                      width: 40, height: 40, borderRadius: "50%", border: "1px solid #E0E0E0",
                      display: "flex", alignItems: "center", justifyContent: "center"
                    }}>
                      <img src={icons.arrowrightBlack} alt="arrow" />
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          )
        )}
      </Box>

      {/* Modals remain unchanged */}
      <PassPlayerModal open={passModal} isJitoTournament={isJitoTournament} onClose={() => setPassModal(false)} onConfirm={handlePass} player={player} isLoading={isPassLoading} profile={player?.playerId?.profilePic || defaultProfile} playerNumber={teamMembers?.find(e => e?.playerId?._id === player?.playerId?._id)?.playerId?.uniqueId} />
      <NextPlayerModal open={nextPlayerModal && !endPurchase} isJitoTournament={isJitoTournament} onClose={() => setNextPlayerModal(false)} onConfirm={() => setNextPlayerModal(false)} player={player} playerNumber={teamMembers?.find(e => e?.playerId?._id === player?.playerId?._id)?.playerId?.uniqueId} />
      <SuccessModal open={passSuccessModal} message="Player kept unsold" />
      <SuccessModal open={soldSuccessModal} sold={true} message={tournamentDetails?.data?.tournament?.teams?.find(e => selectTeam?.teamId === e?._id)?.teamName} />
      <SoldModal open={soldModal} onClose={() => { setSoldModal(false); setSelectTeam({}); }} onConfirm={() => { if (Object.keys(selectTeam).length === 0) return toast.warn("Team is not selected."); setCofirmSoldModal(true); }} teams={tournamentDetails?.data?.tournament?.auctionDetails?.auctionTeams} teamDetail={tournamentDetails?.data?.tournament?.teams} selectTeam={selectTeam} setSelectTeam={setSelectTeam} playerCounts={playerCounts} currBid={currBid} totalPlayerCount={tournamentDetails?.data?.tournament?.teamPlayerCount} baseBid={baseBid} />
      <SoldConfirmModal open={confirmSoldModal} onClose={() => { setCofirmSoldModal(false); setSoldModal(false); }} onConfirm={handleSold} player={player} playerNumber={teamMembers?.find(e => e?.playerId?._id === player?.playerId?._id)?.playerId?.uniqueId} team={tournamentDetails?.data?.tournament?.teams?.filter(e => selectTeam?.teamId === e?._id)} isLoading={sellLoading} />
      <EndAuctionModal open={endModal} playersSold={playersSold} playersUnsold={playersUnsold} onEnd={handleEnd} onReAuction={handleReauction} />
      <ProfileModal open={profileModal} onClose={() => setProfileModal(false)} isJitoTournament={isJitoTournament} profile={player?.playerId?.profilePic || defaultProfile} player={player} playerNumber={teamMembers?.find(e => e?.playerId?._id === player?.playerId?._id)?.playerId?.uniqueId} />
      <ConfirmModal open={undoModal} onClose={() => setUndoModal(false)} title="Are you sure you want to undo last auction performed?" onConfirm={handleUndo} isLoading={undoLoading} />
      <WarningModal open={endPurchase} title="All teams have purchased maximum players. Click Continue to End the Auction" onConfirm={handleEnd} />
    </Box >
  );
};

export default AuctionBidding;